import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocale, useUserProfile } from "@hooks";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@material-ui/core";
import { useCreateConsultantOpportunityMutation } from "@bonliva-crm/graphql";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import { yupResolver } from "@hookform/resolvers/yup";
import { ConsultantOpportunityForm } from "./consultant-opportunity-form";
import { formCreateSchema, OpportunityFormData } from "./utils";

type Props = {
  open: boolean;
  consultantId: string;
  onClose: () => void;
};

export function CreateOpportunityFormModal(props: Props) {
  const user = useUserProfile();
  const { showError, showSuccess } = useSnackbarsPipeline();
  const { locale } = useLocale();

  const form = useForm<OpportunityFormData>({
    resolver: yupResolver(formCreateSchema)
  });

  const [createOpportunity, createOpportunityResult] =
    useCreateConsultantOpportunityMutation({
      onCompleted: () => {
        showSuccess("Presentation card created");
        props.onClose();
      },
      onError: (e) => {
        showError("Failed to create presentation card");
        console.error(e);
      }
    });

  useEffect(() => {
    if (props.open) {
      form.reset({
        consultantId: props.consultantId,
        customer: null,
        note: "",
        areaOfExpertise: null,
        costCenter: null,
        weeks: [],
        type: null,
        estimatedResponseDate: null
      });
    }
  }, [form, props.open]);

  const submit = () => {
    const values = form.getValues();

    return createOpportunity({
      variables: {
        input: {
          customerId: values.customer._id,
          note: values.note ?? "",
          areaOfExpertise: values.areaOfExpertise?.value ?? null,
          costCenter: values.costCenter?.value ?? null,
          weeks: values.weeks,
          estimatedResponseDate: values.estimatedResponseDate,
          consultantId: values.consultantId,
          users: values.user ? [values.user?.value, user.id] : [user.id]
        }
      }
    });
  };

  return (
    <FormProvider {...form}>
      <Dialog open={props.open} onClose={props.onClose} maxWidth="md" fullWidth>
        <DialogTitle>Skapa presentation</DialogTitle>

        <DialogContent>
          <ConsultantOpportunityForm
            consultantId={props.consultantId}
            withUsersSelector
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={props.onClose} variant="contained">
            {locale.Cancel}
          </Button>

          <Button
            onClick={() => submit()}
            disabled={!form.formState.isValid || createOpportunityResult.loading}
            variant="contained"
            color="primary"
          >
            {locale.Save}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
